// src/pages/WebQRCode.js
import React, { useEffect, useState, useRef, useCallback } from "react";
import { QRCodeCanvas } from "qrcode.react";
import html2canvas from "html2canvas";
import axios from "axios";
import { useLocation } from "react-router-dom";

const BASE_URL = process.env.REACT_APP_SERVER_URL || "https://food.stall-centralsoft.com";
const FRONTEND_URL = process.env.REACT_APP_FRONTEND_URL || "https://food.stall-centralsoft.com";

export default function WebQRCode() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);

  // ✅ Query params first, fallback to localStorage
  const stallIDFromParams = params.get("stallID");
  const stallNameFromParams = params.get("stallName");
  const stallID = stallIDFromParams || localStorage.getItem("stallID");
  const stallName = stallNameFromParams || localStorage.getItem("username");

  const [logoUrl, setLogoUrl] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadStatus, setUploadStatus] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);

  // ----------------------
  // Save query params to localStorage
  // ----------------------
  useEffect(() => {
    if (stallIDFromParams) localStorage.setItem("stallID", stallIDFromParams);
    if (stallNameFromParams) localStorage.setItem("username", stallNameFromParams);
  }, [stallIDFromParams, stallNameFromParams]);

  // ----------------------
  // Detect logo on server
  // ----------------------
  const detectLogoFile = useCallback(async () => {
    if (!stallID) return null;
    const extensions = ["jpg", "jpeg", "png", "webp"];
    for (const ext of extensions) {
      const url = `${BASE_URL}/uploads/${stallID}-logo.${ext}`;
      try {
        const res = await fetch(url, { method: "HEAD" });
        if (res.ok) return url;
      } catch (e) {}
    }
    return null;
  }, [stallID]);

  const loadLogo = useCallback(async () => {
    setUploadStatus("🔄 Loading logo...");
    const detectedLogo = await detectLogoFile();
    if (detectedLogo) {
      setLogoUrl(detectedLogo);
      setUploadStatus("✅ Logo loaded");
    } else {
      setLogoUrl("");
      setUploadStatus("⚠️ No logo found");
    }
  }, [detectLogoFile]);

  useEffect(() => {
    if (stallID) loadLogo();
  }, [stallID, loadLogo]);

  const forceReloadLogo = () => loadLogo();

  // ----------------------
  // File Upload
  // ----------------------
  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    const validTypes = ["image/jpeg", "image/png", "image/gif", "image/svg+xml", "image/webp"];
    if (!validTypes.includes(file.type)) {
      setUploadStatus("❌ Please select a valid image file (JPEG, PNG, GIF, SVG, WebP)");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setUploadStatus("❌ File too large. Max 5MB");
      return;
    }
    setSelectedFile(file);
    setLogoUrl(URL.createObjectURL(file));
    setUploadStatus(`📸 Preview ready: ${file.name} (${Math.round(file.size / 1024)} KB)`);
  };

  const handleUpload = async () => {
    if (!selectedFile) return setUploadStatus("❌ Please select a file first");
    if (!stallID) return setUploadStatus("❌ Stall ID missing");

    setIsUploading(true);
    setUploadStatus("⏳ Uploading...");

    try {
      const formData = new FormData();
      formData.append("logo", selectedFile);
      formData.append("stallID", stallID);

      const response = await axios.post(`${BASE_URL}/api/admin/upload/logo`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 30000,
      });

      if (response.data.success) {
        const uploadedFilename = response.data.filename || `${stallID}-logo.${selectedFile.name.split('.').pop()}`;
        setUploadStatus(`✅ Upload successful: ${uploadedFilename}`);
        if (logoUrl.startsWith("blob:")) URL.revokeObjectURL(logoUrl);
        setSelectedFile(null);
        if (fileInputRef.current) fileInputRef.current.value = "";
        setTimeout(() => loadLogo(), 2000);
      } else {
        setUploadStatus(`❌ Upload failed: ${response.data.message || "Unknown"}`);
      }
    } catch (err) {
      console.error(err);
      setUploadStatus("❌ Upload failed. Check network/backend");
    } finally {
      setIsUploading(false);
    }
  };

  // ----------------------
  // QR Functions
  // ----------------------
  const qrUrl = stallID && stallName ? `${FRONTEND_URL}/mobile-order-page?stallID=${encodeURIComponent(stallID)}&stallName=${encodeURIComponent(stallName)}` : "";

  const handleDownload = async () => {
    const qrDiv = document.getElementById("qr-container");
    if (!qrDiv) return alert("QR container not found");
    try {
      setUploadStatus("⏳ Generating image...");
      const canvas = await html2canvas(qrDiv, { useCORS: true, scale: 4, backgroundColor: "#fff" });
      const link = document.createElement("a");
      link.download = `${stallName?.replace(/\s+/g, "_") || "stall"}_Menu_QRCode.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
      setUploadStatus(`✅ Download started`);
      setTimeout(() => setUploadStatus(""), 3000);
    } catch (err) {
      console.error(err);
      setUploadStatus("❌ Download failed");
    }
  };

  // ----------------------
  // Logo Display
  // ----------------------
  const LogoImage = () => {
    if (!logoUrl) return (
      <div style={{ width:"120px", height:"120px", margin:"0 auto", border:"2px dashed #ccc", borderRadius:"8px", display:"flex", alignItems:"center", justifyContent:"center", color:"#999" }}>
        <div>🏪</div>
        <div style={{ fontSize:"12px" }}>No Logo</div>
      </div>
    );
    return <img src={logoUrl} alt="Stall Logo" crossOrigin="anonymous" style={{ width:"120px", height:"120px", objectFit:"contain", borderRadius:"8px", margin:"0 auto", display:"block" }} onError={() => setLogoUrl("")} />;
  };

  // ----------------------
  // Render
  // ----------------------
  if (!stallID || !stallName) {
    return (
      <div className="text-center mt-5">
        <h3>❌ Invalid QR Code</h3>
        <p>Please scan the QR code provided by the stall.</p>
      </div>
    );
  }

  return (
    <div className="container mt-4">
      <div className="text-center mb-4">
        <h1 className="text-primary">📱 QR Code Generator</h1>
        <p className="lead">Create QR Code for Mobile Food Ordering</p>
        <div className="alert alert-info">
          <strong>Stall:</strong> {stallName} | <strong>ID:</strong> {stallID}
        </div>
      </div>

      <div className="row">
        {/* Left - QR */}
        <div className="col-lg-6 mb-4">
          <div className="card shadow-lg h-100">
            <div className="card-header bg-primary text-white"><h4 className="mb-0">QR Code Preview</h4></div>
            <div className="card-body text-center">
              <div id="qr-container" style={{ padding:"25px", background:"linear-gradient(135deg,#f8f9fa,#e9ecef)", borderRadius:"12px", border:"4px solid #667eea", marginBottom:"20px" }}>
                <h3 style={{ fontWeight:"bold", marginBottom:"20px", color:"#d63031", fontSize:"22px", textTransform:"uppercase" }}>{stallName}</h3>
                <div style={{ display:"inline-block", padding:"12px", background:"white", borderRadius:"8px", boxShadow:"0 4px 8px rgba(0,0,0,0.1)", margin:"15px 0" }}>
                  {qrUrl && <QRCodeCanvas value={qrUrl} size={220} level="H" includeMargin fgColor="#d63031" bgColor="#fff" imageSettings={{ src: logoUrl, height:60, width:60, excavate:true, crossOrigin:"anonymous" }} />}
                </div>
                <div style={{ margin:"20px 0", minHeight:"140px" }}><LogoImage /></div>
                <p style={{ fontWeight:"bold", fontSize:"18px", marginTop:"15px", color:"#333", marginBottom:"5px" }}>SCAN TO ORDER FOOD 請掃碼訂餐</p>
                <p style={{ fontSize:"12px", color:"#666", wordBreak:"break-all", backgroundColor:"#f8f9fa", padding:"8px", borderRadius:"4px" }}>{qrUrl}</p>
              </div>

              <div className="d-flex flex-wrap justify-content-center gap-2">
                <button className="btn btn-success btn-sm" onClick={handleDownload}>📥 Download</button>
                <button className="btn btn-info btn-sm" onClick={forceReloadLogo}>🔄 Reload</button>
              </div>

              {uploadStatus && <div className="alert alert-info mt-2">{uploadStatus}</div>}
            </div>
          </div>
        </div>

        {/* Right - Logo */}
        <div className="col-lg-6 mb-4">
          <div className="card shadow h-100">
            <div className="card-header bg-success text-white"><h4 className="mb-0">🖼️ Logo Management</h4></div>
            <div className="card-body">
              <input type="file" ref={fileInputRef} accept="image/*" onChange={handleFileSelect} style={{ display:"none" }} />
              <div className="row g-2 mb-3">
                <div className="col-md-6"><button className="btn btn-outline-primary w-100" onClick={()=>fileInputRef.current?.click()} disabled={isUploading}>📁 Choose Image</button></div>
                <div className="col-md-6"><button className="btn btn-success w-100" onClick={handleUpload} disabled={!selectedFile || isUploading}>{isUploading ? "⏳ Uploading..." : "⬆️ Upload Logo"}</button></div>
              </div>
              {logoUrl && <div className="alert alert-light"><strong>Current Logo:</strong><br/><code>{logoUrl.split("/").pop()}</code><br/><a href={logoUrl} target="_blank" rel="noopener noreferrer">🔗 Open in new tab</a></div>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
